import Constants from '@/Constants';
import { wasApprovedOnCourse } from '@/lib/course';
import { ClassEnrollment } from '@/models/ClassEnrollment';
import { Person } from '@/models/Person';
import { ProgramClass } from '@/models/ProgramClass';
import { Workshop } from '@/models/Workshop';
import { Favorite } from '@/store/favorite/types';
import { CourseStudentView } from '@/store/list/types';
import { LocaleData } from '@/translations/LocaleData';
import { isBefore } from 'date-fns';
import { all } from 'underscore';

export class ProgramService {
    getProgramStatusLabel = (classEnrollmentStatus: string, strings: LocaleData): string => {
        if (!classEnrollmentStatus) return '';

        switch (classEnrollmentStatus) {
            case Constants.classEnrollmentStatus.NOT_STARTED:
                return strings.NOT_ATTEMPTED;
            case Constants.classEnrollmentStatus.NOT_COMPLETED:
                return strings.INCOMPLETE;
            case Constants.classEnrollmentStatus.COMPLETED:
                return strings.COMPLETED_COURSE;
            case Constants.classEnrollmentStatus.PASSED:
                return strings.PASSED;
            case Constants.classEnrollmentStatus.FAILED:
                return strings.FAILED;
            case Constants.classEnrollmentStatus.EXPIRED:
                return strings.COURSE_EXPIRED;
            default:
                return '';
        }
    };

    getProgramProgressPercentage = (amountOfCourses: number, completedCourses: number) => {
        if (isNaN(amountOfCourses) || isNaN(completedCourses)) return 0;
        return Math.round((completedCourses / amountOfCourses) * 100);
    };

    programIsExpired = (finishDateAsString: string): boolean =>
        finishDateAsString !== null && isBefore((finishDateAsString as unknown) as Date, Date.now());

    getProgramWorkload = (courseStudentViews: CourseStudentView[], programClass: ProgramClass): number => {
        const sumRegularCousesWorkload = (courseStudentViews || []).reduce(
            (acc, courseStudentView) => acc + (courseStudentView.course.workload || 0),
            0,
        );

        const sumWorkshopCousesWorkload = this.getProgramWorkshopsWorkload(programClass.workshops || []);

        return sumRegularCousesWorkload + sumWorkshopCousesWorkload;
    };

    canItShowCertificate = (classEnrollment: ClassEnrollment, courseStudentViews: CourseStudentView[]): boolean => {
        const programClass = classEnrollment.programClass;
        return (
            Boolean(programClass.hasProgramCertification) &&
            classEnrollment.status === 'COMPLETED' &&
            all(courseStudentViews, (course) => wasApprovedOnCourse(course.courseEnrollment))
        );
    };

    isFavorited = (programId: number, favoriteList: Favorite[]): boolean => {
        const resultList = favoriteList.filter(
            (item: Favorite) => item.contentType === Constants.entityTypes.PROGRAM && item.contentId === programId,
        );
        return resultList.length > 0;
    };

    certificateUrl = (person: Person, classEnrollment: ClassEnrollment, token: string | null): string => {
        if (!token) {
            throw new Error('Token not specified');
        }

        return Constants.FLOWLMS_PROGRAM_CERTIFICATE_URI.replace('%personId', (person.id || 0).toString())
            .replace('%classEnrollmentId', classEnrollment.id.toString())
            .replace('%token', token);
    };

    getProgramWorkshopsWorkload = (workshops: Workshop[]): number =>
        workshops.reduce((acc, workshop) => {
            return acc + (workshop?.workload || 0);
        }, 0);
}

export const programService = new ProgramService();
